import { toDateString } from '@/core/utils/date';
import { declensionString } from '@/core/utils/string';

export const errorTypes = {
  LockResourceFailedError: 'LockResourceFailedError',
  RoomCategoryShortNameConflictError: 'RoomCategoryShortNameConflictError',
  ShortNameConflictError: 'ShortNameConflictError',
  OperationForbiddenError: 'OperationForbiddenError',
  LegalTINAndKPPConflictError: 'LegalTINAndKPPConflictError',
  IncorrectQuotaChangeOperationError: 'IncorrectQuotaChangeOperationError',
  TariffScheduleServiceGroupMaxNumberExceedError: 'TariffScheduleServiceGroupMaxNumberExceedError',
  TariffScheduleShortNameConflictError: 'TariffScheduleShortNameConflictError',
  UserAlreadyBelongsToSupplierError: 'UserAlreadyBelongsToSupplierError',
  RoomCategoriesForAddingNotFoundError: 'RoomCategoriesForAddingNotFoundError',
  HotelMaxOnApprovalConflictError: 'HotelMaxOnApprovalConflictError',
  QuotaPricesRequiredError: 'QuotaPricesRequiredError',
  QuotaNotFilledError: 'QuotaNotFilledError',
  TravelLineExternalIntegrationIssueError: 'TravelLineExternalIntegrationIssueError',
  ChannelManagerAdditionalMappingRequiredError: 'ChannelManagerAdditionalMappingRequiredError',
  TravelLineTouristTypesHasGapsError: 'TravelLineTouristTypesHasGapsError',
  TravelLineFulfillmentErrorError: 'TravelLineFulfillmentErrorError',
  OrderIsConfirmedError: 'OrderIsConfirmedError',
  OrderIsAvoidedError: 'OrderIsAvoidedError',
  TravelLineHotelNotFoundError: 'TravelLineHotelNotFoundError',
  RestrictionPeriodOverlapConflictError: 'RestrictionPeriodOverlapConflictError',
  SupplierQuestionnaireNotUpdatedError: 'SupplierQuestionnaireNotUpdatedError',
  OperationOnUnlockedResourceIsInvalidError: 'OperationOnUnlockedResourceIsInvalidError',
  InvitationNotFoundError: 'InvitationNotFoundError',
  AccessRequestInvalidStatusError: 'AccessRequestInvalidStatusError',
};

// error types which do not show by regular error alert
const customErrorTypes = new Set([
  errorTypes.QuotaPricesRequiredError,
  errorTypes.QuotaNotFilledError,
  errorTypes.TravelLineExternalIntegrationIssueError,
  errorTypes.ChannelManagerAdditionalMappingRequiredError,
  errorTypes.TravelLineTouristTypesHasGapsError,
  errorTypes.TravelLineFulfillmentErrorError,
  errorTypes.OrderIsConfirmedError,
  errorTypes.OrderIsAvoidedError,
  errorTypes.TravelLineHotelNotFoundError,
  errorTypes.RestrictionPeriodOverlapConflictError,
  errorTypes.OperationOnUnlockedResourceIsInvalidError,
  errorTypes.InvitationNotFoundError,
  errorTypes.AccessRequestInvalidStatusError,
  errorTypes.UserAlreadyBelongsToSupplierError,
]);

// error types which show as warning alert
const warningErrorTypes = new Set([
  errorTypes.LockResourceFailedError,
  errorTypes.RoomCategoryShortNameConflictError,
  errorTypes.ShortNameConflictError,
  errorTypes.OperationForbiddenError,
  errorTypes.LegalTINAndKPPConflictError,
  errorTypes.IncorrectQuotaChangeOperationError,
  errorTypes.TariffScheduleShortNameConflictError,
  errorTypes.RoomCategoriesForAddingNotFoundError,
  errorTypes.HotelMaxOnApprovalConflictError,
  errorTypes.SupplierQuestionnaireNotUpdatedError,
]);

export const isWarningErrorType = (errorType: string) => warningErrorTypes.has(errorType);

export const isCustomErrorType = (errorType: string) => customErrorTypes.has(errorType);

const snackErrorTexts: Record<
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (args: any) => {
    title?: string | undefined;
    message: string;
  }
> = {
  LockResourceFailedError: (error: {
    blockedInfo: {
      blockers: {
        userAlias: string;
        lockTime: string;
      }[];
    };
  }) => ({
    title: 'Невозможно перейти в режим редактирования',
    message: `Ресурс заблокирован другим пользователем.<br/><b>Пользователь:</b> ${error.blockedInfo.blockers[0]!.userAlias}<br><b>Время блокировки:</b> ${toDateString(error.blockedInfo.blockers[0]!.lockTime, undefined, 'DD.MM.YYYY H:mm:ss')}`,
  }),
  OperationForbiddenError: () => ({
    message: 'Выполнение операции невозможно.',
  }),
  LegalTINAndKPPConflictError: () => ({
    message: 'Юр.лицо с данной парой ИНН и КПП уже существует.',
  }),
  IncorrectQuotaChangeOperationError: ({ roomCategoryName, from, to, requiredReserve, booked }) => ({
    title: 'Невозможно установить Квоту',
    message: `Для Категории Номера "${roomCategoryName}" в период
  ${toDateString(from)}-${toDateString(to)} невозможно установить Квоту
    в размере ${requiredReserve} ${declensionString(requiredReserve, ['номера', 'номеров', 'номеров'])} из-за наличия бронирований в размере ${booked} ${declensionString(booked, ['номера', 'номеров', 'номеров'])}.`,
  }),
  TariffScheduleServiceGroupMaxNumberExceedError: () => ({
    message: 'Достигнуто максимальное количество групп услуг в тарифном плане',
  }),
  RoomCategoriesForAddingNotFoundError: () => ({
    message: 'Отсутствуют категории номеров для добавления',
  }),
  HotelMaxOnApprovalConflictError: () => ({
    message: 'Максимальное количество отелей на рассмотрении не может превышать 2',
  }),
  SupplierQuestionnaireNotUpdatedError: () => ({
    title: 'Невозможно внести изменения в Анкету',
    message: 'Ваша заявка была обработана администратором. Перезагрузите страницу, чтобы увидеть изменения',
  }),
} as const;

export type SnackErrorTextsKeys = keyof typeof snackErrorTexts;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getErrorSnackText = (errorType: keyof typeof snackErrorTexts, args: any) =>
  snackErrorTexts[errorType](args);
